import type { ProductSeriesPoint, SalesRecord } from "../types";

const DAY_MS = 86_400_000;
const FORECAST_HORIZON = 30;
const CHART_HORIZON = 14;

export interface RegressionModel {
  slope: number;
  intercept: number;
  r2: number;
  slopePercent: number;
}

export interface DemandForecast {
  series: ProductSeriesPoint[];
  future: ProductSeriesPoint[];
  quantities: number[];
  fitted: number[];
  forecasts: number[];
  lower: number[];
  upper: number[];
  regression: RegressionModel;
  avg3d: number;
  avg7d: number;
  recentHeat: number;
  forecastDaily: number;
  forecastLowerDaily: number;
  forecastUpperDaily: number;
  periodDays: number;
  periodConfidence: number;
  shockDays: string[];
  riskNote: string;
  confidence: "高" | "中" | "低";
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function mean(values: number[]) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function addCalendarDays(date: string, days: number) {
  const next = new Date(`${date}T12:00:00+08:00`);
  next.setDate(next.getDate() + days);
  return next.toISOString().slice(0, 10);
}

function labelFor(date: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(`${date}T12:00:00+08:00`));
}

function calendarDates(from: string, to: string) {
  const dates: string[] = [];
  const start = new Date(`${from}T12:00:00+08:00`).getTime();
  const end = new Date(`${to}T12:00:00+08:00`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return dates;
  for (let time = start; time <= end; time += DAY_MS) {
    dates.push(new Date(time).toISOString().slice(0, 10));
  }
  return dates;
}

function median(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function quantile(values: number[], ratio: number) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * clamp(ratio, 0, 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function linearRegression(values: number[]) {
  if (!values.length) return { slope: 0, intercept: 0, r2: 0, slopePercent: 0 };
  if (values.length === 1) {
    return { slope: 0, intercept: values[0], r2: 0, slopePercent: 0 };
  }
  const xMean = (values.length - 1) / 2;
  const yMean = mean(values);
  let covariance = 0;
  let variance = 0;
  values.forEach((value, index) => {
    covariance += (index - xMean) * (value - yMean);
    variance += (index - xMean) ** 2;
  });
  const slope = variance ? covariance / variance : 0;
  const intercept = yMean - slope * xMean;
  const total = values.reduce((sum, value) => sum + (value - yMean) ** 2, 0);
  const residual = values.reduce(
    (sum, value, index) => sum + (value - (intercept + slope * index)) ** 2,
    0,
  );
  return {
    slope,
    intercept,
    r2: total ? clamp(1 - residual / total, 0, 1) : 0,
    slopePercent: yMean ? (slope / yMean) * 100 : 0,
  };
}

function detectPeriod(values: number[], trend: number) {
  let best = { days: 0, score: 0, confidence: 0 };
  if (values.length < 8) return best;
  const detrended = values.map((value, index) => value - trend * index);
  const center = mean(detrended);
  const centered = detrended.map((value) => value - center);
  const variance = mean(centered.map((value) => value ** 2));
  if (variance < 0.01) return best;

  for (let lag = 2; lag <= 14; lag++) {
    let product = 0;
    let left = 0;
    let right = 0;
    for (let index = lag; index < centered.length; index++) {
      const a = centered[index];
      const b = centered[index - lag];
      product += a * b;
      left += a ** 2;
      right += b ** 2;
    }
    if (left < 0.01 || right < 0.01) continue;
    const overlap = centered.length - lag;
    const score = product / Math.sqrt(left * right);
    const support = clamp(overlap / 14, 0.25, 1);
    const practicalBoost = lag === 7 ? 0.04 : lag === 14 ? 0.02 : 0;
    const adjusted = score * support + practicalBoost;
    if (adjusted > best.score) {
      best = {
        days: lag,
        score: adjusted,
        confidence: clamp((adjusted - 0.16) / 0.58, 0, 1) * support,
      };
    }
  }
  if (best.score < 0.2 || best.confidence < 0.12) {
    return { days: 0, score: 0, confidence: 0 };
  }
  return best;
}

function dampedHolt(values: number[], regression: RegressionModel) {
  const alpha = 0.36;
  const beta = 0.12;
  const phi = 0.88;
  const fitted: number[] = [];
  const residuals: number[] = [];
  const shockFlags: boolean[] = [];
  let level = values[0] ?? 0;
  let trend = regression.slope;
  const structuralScale = Math.max(1, median(values) * 0.18);

  values.forEach((actual, index) => {
    const prediction = Math.max(0, level + phi * trend);
    fitted.push(prediction);
    const error = actual - prediction;
    const window = residuals.slice(-14);
    const robustCenter = median(window);
    const robustScale = Math.max(
      2.5,
      1.4826 * median(window.map((value) => Math.abs(value - robustCenter))) * 2.2,
    );
    const shock = index >= 3 && Math.abs(error - robustCenter) > Math.max(8, robustScale * 2.8);
    shockFlags.push(shock);

    const clippedError = shock
      ? Math.sign(error) * Math.min(Math.abs(error), robustScale * 2.4)
      : error;
    const nextLevel = alpha * actual + (1 - alpha) * (level + phi * trend);
    let nextTrend = beta * (nextLevel - level) + (1 - beta) * phi * trend;
    const maxTrend = Math.max(structuralScale, mean(values) * 0.32);
    nextTrend = clamp(nextTrend, -maxTrend, maxTrend);
    level = Math.max(0, nextLevel);
    trend = nextTrend;
    residuals.push(clippedError);
  });

  return { fitted, residuals, shockFlags, level, trend };
}

function residualInterval(residuals: number[], horizon: number) {
  const center = median(residuals);
  const centered = residuals.map((value) => value - center);
  const low = quantile(centered, 0.08);
  const high = quantile(centered, 0.92);
  const widening = Math.sqrt(1 + Math.max(0, horizon - 1) * 0.11);
  return { lower: low * widening, upper: high * widening };
}

function buildDailySeries(records: SalesRecord[], asOf: string) {
  const daily = new Map<string, number>();
  records.forEach((record) => {
    if (!record.date || record.date > asOf || !Number.isFinite(record.quantity)) return;
    daily.set(record.date, (daily.get(record.date) ?? 0) + Math.max(0, record.quantity));
  });
  const dates = [...daily.keys()].sort();
  if (!dates.length) return [];
  const contiguous = calendarDates(dates[0], asOf);
  return contiguous.map((date) => ({
    date,
    label: labelFor(date),
    quantity: daily.get(date) ?? 0,
    dateKind: "actual" as const,
  }));
}

export function buildDemandForecast(records: SalesRecord[], asOf: string): DemandForecast {
  const series = buildDailySeries(records, asOf);
  const quantities = series.map((point) => point.quantity);
  const regression = linearRegression(quantities);
  if (!series.length) {
    return {
      series,
      future: [],
      quantities,
      fitted: [],
      forecasts: [],
      lower: [],
      upper: [],
      regression,
      avg3d: 0,
      avg7d: 0,
      recentHeat: 0,
      forecastDaily: 0,
      forecastLowerDaily: 0,
      forecastUpperDaily: 0,
      periodDays: 0,
      periodConfidence: 0,
      shockDays: [],
      riskNote: "暂无连续出库记录，无法形成统计预测。",
      confidence: "低",
    };
  }

  const holt = dampedHolt(quantities, regression);
  const period = detectPeriod(quantities, regression.slope);
  const seasonalProfile = new Map<number, number>();
  if (period.days) {
    const bucket = new Map<number, number[]>();
    quantities.forEach((value, index) => {
      const phase = index % period.days;
      const values = bucket.get(phase) ?? [];
      values.push(value - (regression.intercept + regression.slope * index));
      bucket.set(phase, values);
    });
    const profileCenter = mean([...bucket.values()].map(mean));
    bucket.forEach((values, phase) => seasonalProfile.set(phase, mean(values) - profileCenter));
  }

  const forecasts: number[] = [];
  const lower: number[] = [];
  const upper: number[] = [];
  let cumulativeTrend = holt.trend;
  for (let horizon = 1; horizon <= FORECAST_HORIZON; horizon++) {
    const base = Math.max(0, holt.level + cumulativeTrend * Math.pow(0.88, horizon));
    const seasonal = seasonalProfile.get((quantities.length + horizon - 1) % Math.max(1, period.days)) ?? 0;
    const prediction = Math.max(0, base + seasonal * period.confidence);
    const interval = residualInterval(holt.residuals, horizon);
    forecasts.push(prediction);
    lower.push(Math.max(0, prediction + interval.lower));
    upper.push(Math.max(0, prediction + interval.upper));
    cumulativeTrend *= 0.88;
  }

  const future = forecasts.slice(0, CHART_HORIZON).map((forecast, index) => {
    const date = addCalendarDays(asOf, index + 1);
    return {
      date,
      label: labelFor(date),
      quantity: 0,
      forecast,
      lower: lower[index],
      upper: upper[index],
      fitted: forecast,
      dateKind: "forecast" as const,
    };
  });

  const enrichedSeries = series.map((point, index) => ({
    ...point,
    fitted: holt.fitted[index] ?? point.quantity,
    shock: holt.shockFlags[index],
  }));
  const last3 = quantities.slice(-3);
  const last7 = quantities.slice(-7);
  const avg3d = mean(last3);
  const avg7d = mean(last7);
  const recentHeat = avg3d * 0.62 + avg7d * 0.38;
  const chartWindow = forecasts.slice(0, 7);
  const forecastDaily = mean(chartWindow);
  const forecastLowerDaily = mean(lower.slice(0, 7));
  const forecastUpperDaily = mean(upper.slice(0, 7));
  const shockDays = enrichedSeries.filter((point) => point.shock).map((point) => point.date);
  const coverage = quantities.length / Math.max(1, series.length);
  const confidence: DemandForecast["confidence"] =
    quantities.length >= 21 && regression.r2 >= 0.28 && coverage >= 0.85
      ? "高"
      : quantities.length >= 10
        ? "中"
        : "低";
  const riskNote = shockDays.length
    ? `检出 ${shockDays.length} 个脉冲日，预测已用稳健残差区间吸收突变。`
    : period.days
      ? `检出约 ${period.days} 日需求周期，预测已叠加周期项。`
      : "未检出稳定周期，预测以阻尼趋势为主。";

  return {
    series: enrichedSeries,
    future,
    quantities,
    fitted: holt.fitted,
    forecasts,
    lower,
    upper,
    regression,
    avg3d,
    avg7d,
    recentHeat,
    forecastDaily,
    forecastLowerDaily,
    forecastUpperDaily,
    periodDays: period.days,
    periodConfidence: period.confidence,
    shockDays,
    riskNote,
    confidence,
  };
}

export function cumulativeForecast(forecast: DemandForecast, days: number) {
  const horizon = Math.max(1, Math.round(days));
  const extend = (values: number[]) => {
    const rows = values.slice(0, horizon);
    const last = rows.at(-1) ?? 0;
    while (rows.length < horizon) rows.push(last);
    return rows.reduce((sum, value) => sum + value, 0);
  };
  return {
    mean: extend(forecast.forecasts),
    lower: extend(forecast.lower),
    upper: extend(forecast.upper),
  };
}

export function daysOfStock(forecast: DemandForecast, stock: number) {
  if (stock <= 0) return 0;
  let remaining = stock;
  for (let day = 0; day < forecast.forecasts.length; day++) {
    remaining -= forecast.forecasts[day];
    if (remaining <= 0) return day + Math.max(0, 1 + remaining / Math.max(1, forecast.forecasts[day]));
  }
  const average = Math.max(0.01, mean(forecast.forecasts.slice(-7)));
  return forecast.forecasts.length + remaining / average;
}

export function latestFiniteValue(
  records: SalesRecord[],
  field: "currentStock" | "incomingStock",
  asOf: string,
) {
  return records
    .filter((record) => record.date <= asOf && Number.isFinite(record[field]))
    .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id))
    .map((record) => record[field])
    .find((value): value is number => Number.isFinite(value)) ?? 0;
}
